import {
  CheckboxIconProps,
  Checkbox as NextUICheckbox,
  CheckboxProps as NextUICheckboxProps,
} from "@heroui/react";
import clsx from "clsx";

type Color = Extract<NextUICheckboxProps["color"], "primary">;
type Size = Extract<NextUICheckboxProps["size"], "sm" | "md" | "lg">;

export type CheckboxProps = Omit<
  NextUICheckboxProps,
  "color" | "onClick" | "children"
> & {
  color?: Color;
  label: string;
  onClick?: never;
  size?: Size;
  ref?: React.Ref<HTMLInputElement>;
};

// We're doing this due to https://recare.atlassian.net/browse/DEV-15907
function CustomIcon({
  height,
  size,
  width,
  ...props
}: CheckboxIconProps & { height?: number; size?: number; width?: number }) {
  // avoid passing non-DOM attributes to svg
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disableAnimation, isIndeterminate, isSelected, ...otherProps } =
    props;

  if (isIndeterminate) {
    return (
      <svg
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="4"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        {...otherProps}
        className={clsx("lucide lucide-minus", otherProps.className)}
        height={size || height || 24}
        width={size || width || 24}
      >
        <path d="M5 12h14" />
      </svg>
    );
  }

  return (
    <svg
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="4"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...otherProps}
      className={clsx("lucide lucide-check", otherProps.className)}
      height={size || height || 24}
      width={size || width || 24}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function Checkbox({
  color = "primary",
  label,
  ref,
  size = "sm",
  ...props
}: CheckboxProps) {
  return (
    <NextUICheckbox
      {...props}
      ref={ref}
      classNames={{
        base: clsx("opacity-100", {
          "pointer-events-none": props.isDisabled,
        }),
        icon: clsx("", {
          "h-3.5 w-3": size === "sm",
          "h-3.75 w-4": size === "md",
          "h-4 w-5": size === "lg",
        }),
        label: clsx("text-default-900", {
          "after:ms-0.5 after:content-['*']": props.isRequired,
          "inline-flex items-center justify-center before:absolute before:h-0.5 before:w-0 before:bg-foreground before:content-[''] group-data-[selected=true]:opacity-60 group-data-[selected=true]:before:w-full":
            props.lineThrough,
          "text-danger": props.isInvalid,
          "text-large": size === "lg",
          "text-small": size === "sm" || size === "md",
        }),
        wrapper: clsx("", {
          "before:border-danger": props.isInvalid,
          "before:border-gray-500 before:bg-gray-200 after:bg-gray-600 after:text-gray-200":
            props.isDisabled,
          "h-4 w-4 rounded-[4px] before:rounded-[4px] after:rounded-[4px]":
            size === "sm",
          "h-5 w-5 rounded-[5px] before:rounded-[5px] after:rounded-[5px]":
            size === "md",
          "h-6 w-6 rounded-[6px] before:rounded-[6px] after:rounded-[6px]":
            size === "lg",
          "text-primary-foreground after:bg-primary after:text-primary-foreground group-data-[hover=true]:before:border-primary group-data-[hover=true]:before:bg-primary-light":
            color === "primary",
        }),
      }}
      data-testid={`${props.name}${props.value ? `-${props.value}` : ""}`}
      icon={(props) => <CustomIcon {...props} />}
    >
      {label}
    </NextUICheckbox>
  );
}

Checkbox.displayName = "RecareUI.Checkbox";
